import { useContext } from 'react';
import { useInterpret, useSelector } from '@xstate/react';
import { assign } from 'xstate';

import { DataAccessContext } from '@chargify/data-access';

import {
  changePaymentProfile,
  SetErrorEvent,
} from '../machines/changePaymentProfile';
import { useServices } from './useServices';
import { paymentProfilesSelectors } from '../machines';
import { PaymentProfilesContext } from '../contexts';

type Options = {
  subscriptionId: number;
  paymentProfileId: number;
  catalogId: number;
  onChangeSuccess: () => void;
};
/**
 * Configures machine responsible for changing payment profile
 * @param {string} formElementId - HTML element used to tokenize payment method
 * @param {number} subscriptionId - subscription identifier
 * @param {number} catalogId - product identifier
 * @param {() => void} onChangeSuccess - callback to be called when payment profile is created
 * @returns {Record} payment profile change service
 */
export const useChangePaymentProfile = ({
  subscriptionId,
  paymentProfileId,
  catalogId,
  onChangeSuccess,
}: Options) => {
  const { dataAccess } = useContext(DataAccessContext);
  const { paymentProfilesService } = useContext(PaymentProfilesContext);
  const { subscriptionsService } = useServices();
  const paymentProfile = useSelector(
    paymentProfilesService,
    paymentProfilesSelectors.getPaymentProfilesById(paymentProfileId)
  );
  return useInterpret(changePaymentProfile, {
    actions: {
      setError: assign((context, event: SetErrorEvent) => {
        return {
          ...context,
          error: event.data,
        };
      }),
      onChangeProfileSuccess: () => {
        paymentProfilesService.send({
          type: 'CHANGE_PAYMENT_PROFILE',
          paymentProfileId,
        });
        subscriptionsService.send({
          type: 'UPDATE_SUBSCRIPTION',
          subscriptionId,
          catalogId,
          billing: {
            paymentProfileConnected: true,
            paymentProfileId: paymentProfile.id,
            ...paymentProfile,
          },
        });
        onChangeSuccess();
      },
    },
    services: {
      changePaymentProfile: () => {
        return dataAccess.updateSubscriptionPaymentProfile(
          paymentProfileId,
          subscriptionId
        );
      },
    },
  });
};
